import {
  Button,
  ButtonAnchor,
  ButtonLink,
  IconSet,
  Tooltip,
} from '@postscript/components';

import { useGlobalModal } from 'components/GlobalModal/globalModal';
import { TooltipPortal } from 'components/shopper/components/shared/TooltipPortal';
import { useUser } from 'controllers/contexts/user';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';

import InfinityTestingModal from 'components/billing/modules/plans/InfinityTestingModal';
import { BRAND_CENTER_PATH } from 'components/brand/constants';
import type { WorkflowContent } from 'providers/ProgressiveOnboarding/types';
import {
  logButtonClickEvent,
  logLinkClickEvent,
  PRODUCT_AREAS,
} from 'utils/events';

import {
  BRAND_CENTER_HELP_LINK,
  BRAND_CENTER_SAVED_STEP_KEY,
  COMPLETE_BRAND_CENTER_STEP_TOOLTIP_ID,
  COMPLIANCE_OVERVIEW_LINK,
  COMPLIANCE_SETTINGS_BASE_PATH,
  COMPLIANCE_SETTINGS_COMPLETED_STEP_KEY,
  INFINITY_TESTING_TRIED_STEP_KEY,
  INTEGRATIONS_BASE_PATH,
  INTEGRATION_ACTIVATED_STEP_KEY,
  PROGRESSIVE_ONBOARDING_SOURCE,
  PS_APP_PROG_ONBOARDING_FEATURE_KEY,
} from './constants';

export const COMPLETE_COMPLIANCE_STEP_CONTENT: WorkflowContent['steps'] = {
  [COMPLIANCE_SETTINGS_COMPLETED_STEP_KEY]: {
    key: COMPLIANCE_SETTINGS_COMPLETED_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Complete Compliance',
    content:
      'To start sending messages, activating automations, campaigns, and popups, ensure all required compliance information is submitted, including Customer Support Phone Number.',
    icon: 'Disc',
    badge: {
      text: 'Required',
      variant: 'info',
    },
    PrimaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonLink
          to={COMPLIANCE_SETTINGS_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.COMPLIANCE,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              step: COMPLIANCE_SETTINGS_COMPLETED_STEP_KEY,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Go To Compliance Settings
        </ButtonLink>
      );
    },
    SecondaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonAnchor
          variant="plain"
          href={COMPLIANCE_OVERVIEW_LINK}
          icon={IconSet.Share}
          iconPosition="right"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            logLinkClickEvent(event, {
              productArea: PRODUCT_AREAS.COMPLIANCE,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: COMPLIANCE_SETTINGS_COMPLETED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          More Help
        </ButtonAnchor>
      );
    },
  },
};

export const ENABLE_INTEGRATION_STEP_CONTENT: WorkflowContent['steps'] = {
  [INTEGRATION_ACTIVATED_STEP_KEY]: {
    key: INTEGRATION_ACTIVATED_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Power Up with Integrations',
    content:
      'Streamline your workflow by integrating essential tools, including email marketing, customer service platforms, cart systems, and more.',
    icon: 'Disc',
    PrimaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonLink
          to={INTEGRATIONS_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.INTEGRATIONS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: INTEGRATION_ACTIVATED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Go to Integrations
        </ButtonLink>
      );
    },
    SecondaryAction: ({ step }) => {
      const { markStepCompleted } = useProgressiveOnboarding();
      const { user } = useUser();

      if (step.state === 'complete') {
        return null;
      }

      return (
        <Button
          variant="text"
          onClick={(event) => {
            markStepCompleted({
              guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              stepKey: INTEGRATION_ACTIVATED_STEP_KEY,
            });

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.INTEGRATIONS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: INTEGRATION_ACTIVATED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Mark as Complete
        </Button>
      );
    },
  },
};

export const SAVE_BRAND_CENTER_STEP_CONTENT: WorkflowContent['steps'] = {
  [BRAND_CENTER_SAVED_STEP_KEY]: {
    key: BRAND_CENTER_SAVED_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Define Your Brand Voice',
    content: (
      <>
        Brand Center comes pre-filled with information from your website and
        social platforms.
        <br />
        <br />
        Review, update, and save settings for Postscript AI features like
        Infinity Testing and Shopper.
      </>
    ),
    icon: 'Disc',
    PrimaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonLink
          to={BRAND_CENTER_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.BRAND_CENTER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              step: BRAND_CENTER_SAVED_STEP_KEY,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Go to Brand Center
        </ButtonLink>
      );
    },
    SecondaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonAnchor
          variant="plain"
          href={BRAND_CENTER_HELP_LINK}
          icon={IconSet.Share}
          iconPosition="right"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            logLinkClickEvent(event, {
              productArea: PRODUCT_AREAS.BRAND_CENTER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: BRAND_CENTER_SAVED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          More Help
        </ButtonAnchor>
      );
    },
  },
};

export const TRY_INFINITY_TESTING_STEP_CONTENT: WorkflowContent['steps'] = {
  [INFINITY_TESTING_TRIED_STEP_KEY]: {
    key: INFINITY_TESTING_TRIED_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Optimize your Messages with Infinity Testing',
    content: (
      <>
        Infinity Testing surpasses traditional A/B testing by continuously
        optimizing hundreds of on-brand message variants, evolving in real time
        to drive the highest performance.
      </>
    ),
    icon: 'Disc',
    PrimaryAction: () => {
      const { user } = useUser();
      const { showModal } = useGlobalModal();

      return (
        <Button
          onClick={(event) => {
            showModal({
              children: <InfinityTestingModal />,
              style: { width: 1130, padding: 0 },
            });
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.INFINITY_TESTING,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              step: INFINITY_TESTING_TRIED_STEP_KEY,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Learn More
        </Button>
      );
    },
    SecondaryAction: ({ step }) => {
      const { markStepCompleted } = useProgressiveOnboarding();
      const { user } = useUser();

      if (step.state === 'complete') {
        return null;
      }

      return (
        <Button
          variant="text"
          onClick={(event) => {
            markStepCompleted({
              guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              stepKey: INFINITY_TESTING_TRIED_STEP_KEY,
            });

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.INFINITY_TESTING,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: INFINITY_TESTING_TRIED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Mark as Complete
        </Button>
      );
    },
    tooltip: (
      <TooltipPortal>
        <Tooltip id={COMPLETE_BRAND_CENTER_STEP_TOOLTIP_ID}>
          Please complete &quot;Define
          <br />
          Your Brand Voice&quot; step first.
        </Tooltip>
      </TooltipPortal>
    ),
    tooltipId: COMPLETE_BRAND_CENTER_STEP_TOOLTIP_ID,
  },
};
